import useExecuteClosePosition from 'src/hooks/positions/useExecuteClosePosition';
import { BigNum } from '@drift-labs/sdk';
import { useCallback } from 'react';
import useModal from 'src/components/Modals/hooks/useModal';

interface Params {
	marketIndex: number;
	baseSize: BigNum;
	onClosePositionSuccess?: () => void;
}

/**
 * When closing market orders, we may either show a confirmation modal if the user
 * hasn't disabled it, or directly close the position. This hook handles that.
 */
export default function useClosePositionWithMarketOrder({
	marketIndex,
	baseSize,
	onClosePositionSuccess,
}: Params) {
	const { show } = useModal();
	const { closePosition, isPending, showClosePositionConfirmation } =
		useExecuteClosePosition({
			marketIndex,
			uiOrderType: 'market',
			baseSize,
			onSuccess: onClosePositionSuccess,
		});

	const closePositionWithMarketOrder = useCallback(async () => {
		// If we shouldn't show the confirmation modal, just close the position immediately.
		if (!showClosePositionConfirmation) {
			await closePosition();
			return;
		}

		show({
			type: 'close_position',
			props: { marketIndex, uiOrderType: 'market' },
		});
	}, [closePosition, marketIndex, show, showClosePositionConfirmation]);

	return {
		closePositionWithMarketOrder,
		isPending,
		showClosePositionConfirmation,
	};
}
